import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { getRulesetFeatures, getAvailableRulesets, getRulesetConfig } from '@character-creator/data';
import { useCharacterCreationStore } from '../../stores/characterCreationStore';
import { useRulesetIdFromUrl } from '../../hooks/useUrlRuleset';
import { useApplyCharacterDraft } from '../../hooks/useApplyCharacterDraft';
import { CHARACTER_TEMPLATES } from '../../utils/randomCharacter';
import { AutoSaveNotificationDialog } from '../ui/AutoSaveNotificationDialog';
import { ImportCharacterDialog } from '../ui/ImportCharacterDialog';
import { saveCharacter } from '../../utils/characterDatabase';
import { hasCharacterData } from '../../utils/draftUtils';
import type { CharacterDraft } from '../../stores/characterCreationStore';
import type { RuntimeState } from '../../utils/characterExport';

interface HamburgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

type PendingAction = 'blank' | 'random' | 'template-fighter' | 'template-wizard' | 'template-rogue' | 'template-cleric' | 'import' | 'switch-ruleset' | null;

export function HamburgerMenu({ isOpen, onClose }: HamburgerMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [templatesExpanded, setTemplatesExpanded] = useState(false);
  const [rulesetsExpanded, setRulesetsExpanded] = useState(false);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [savedCharacterName, setSavedCharacterName] = useState('');
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [pendingRuleset, setPendingRuleset] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Get current ruleset from URL for link prefixing
  const rulesetId = useRulesetIdFromUrl();
  const terminology = getRulesetFeatures(rulesetId).ui.terminology;

  const { draft, reset, setRulesetId } = useCharacterCreationStore();
  const { applyDraft, applyRandomCharacter, applyImportedCharacter } = useApplyCharacterDraft();

  const draftHasData = hasCharacterData(draft);

  // Handle action with auto-save when there's character data
  const handleActionWithConfirm = async (action: PendingAction) => {
    if (draftHasData) {
      // Auto-save the current character
      const result = await saveCharacter(draft);
      if (result.success) {
        setSavedCharacterName(draft.name || 'Unnamed Character');
        setPendingAction(action);
        setShowSaveNotification(true);
      } else {
        // If save fails, just execute the action anyway
        console.error('Failed to auto-save:', result.errors);
        executeAction(action);
      }
    } else {
      executeAction(action);
    }
  };

  // Handle ruleset switch with auto-save if needed
  const handleRulesetSwitch = async (newRulesetId: string) => {
    if (newRulesetId === rulesetId) {
      onClose();
      return; // Already on this ruleset
    }
    if (draftHasData) {
      // Auto-save the current character
      const result = await saveCharacter(draft);
      if (result.success) {
        setSavedCharacterName(draft.name || 'Unnamed Character');
        setPendingRuleset(newRulesetId);
        setPendingAction('switch-ruleset');
        setShowSaveNotification(true);
      } else {
        // If save fails, just execute the switch anyway
        console.error('Failed to auto-save:', result.errors);
        reset();
        navigate(`/${newRulesetId}`);
        onClose();
      }
    } else {
      navigate(`/${newRulesetId}`);
      onClose();
    }
  };

  // Execute the pending action
  const executeAction = (action: PendingAction) => {
    switch (action) {
      case 'blank':
        reset();
        setRulesetId(rulesetId);
        navigate(`/${rulesetId}/create`, { state: { freshStart: true } });
        break;
      case 'random':
        applyRandomCharacter(rulesetId);
        break;
      case 'template-fighter':
        executeTemplate('fighter');
        return; // executeTemplate handles navigation
      case 'template-wizard':
        executeTemplate('wizard');
        return;
      case 'template-rogue':
        executeTemplate('rogue');
        return;
      case 'template-cleric':
        executeTemplate('cleric');
        return;
      case 'import':
        setShowImportDialog(true);
        return; // Don't close menu yet, dialog handles navigation
      case 'switch-ruleset':
        if (pendingRuleset) {
          reset();
          navigate(`/${pendingRuleset}`);
          setPendingRuleset(null);
        }
        break;
    }
    onClose();
  };

  const handleImport = (imported: CharacterDraft, runtimeState?: RuntimeState) => {
    applyImportedCharacter(imported, rulesetId, runtimeState);
    onClose();
  };

  // Close on escape key (but not if dialog is open - dialog handles its own escape)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (showImportDialog || showSaveNotification) return;
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose, showImportDialog, showSaveNotification]);

  // Close on click outside (but not if import dialog is open)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Don't close if import or save notification dialog is open
      if (showImportDialog || showSaveNotification) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      const timer = setTimeout(() => {
        window.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpen, onClose, showImportDialog, showSaveNotification]);

  const executeTemplate = (templateName: string) => {
    // Map menu names to template IDs
    const templateIdMap: Record<string, string> = {
      fighter: 'human-fighter',
      wizard: 'elf-wizard',
      rogue: 'halfling-rogue',
      cleric: 'dwarf-cleric',
    };

    const templateId = templateIdMap[templateName];
    const template = CHARACTER_TEMPLATES.find((t) => t.id === templateId);

    if (template) {
      applyDraft(template.draft, rulesetId);
      navigate(`/${rulesetId}/create`, { state: { freshStart: true } });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-fade-in"
        aria-hidden="true"
      />

      {/* Menu Panel */}
      <div
        ref={menuRef}
        className={clsx(
          'fixed top-0 left-0 z-50 h-full w-72 bg-background-card border-r border-border-default',
          'shadow-2xl transform transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Main menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border-default">
          <h2 className="text-lg font-semibold text-text-primary">Menu</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors"
            aria-label="Close menu"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-80px)]">
          {/* Ruleset Selection - at top for easy access */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setRulesetsExpanded(!rulesetsExpanded)}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors text-left"
            >
              <span className="w-5 h-5 flex-shrink-0">
                <SwitchIcon />
              </span>
              <span className="text-sm font-medium flex-1">Switch Ruleset</span>
              <span className="text-xs text-text-muted">{getRulesetConfig(rulesetId)?.name}</span>
              <ChevronIcon
                className={clsx('w-4 h-4 transition-transform', rulesetsExpanded && 'rotate-180')}
              />
            </button>
            {rulesetsExpanded && (
              <div className="ml-8 mt-1 space-y-1">
                {getAvailableRulesets().map((ruleset) => (
                  <button
                    key={ruleset.id}
                    type="button"
                    onClick={() => handleRulesetSwitch(ruleset.id)}
                    className={clsx(
                      'block w-full px-3 py-2 text-sm rounded-lg text-left transition-colors',
                      ruleset.id === rulesetId
                        ? 'bg-accent-gold/20 text-accent-gold'
                        : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated'
                    )}
                  >
                    {ruleset.name}
                    {ruleset.id === rulesetId && ' ✓'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-border-default my-4" />

          {/* Actions Section */}
          <div className="mb-4">
            <h3 className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
              Create Character
            </h3>
            <MenuItem
              icon={<PlusIcon />}
              label="New Blank Character"
              onClick={() => handleActionWithConfirm('blank')}
            />
            <MenuItem
              icon={<DiceIcon />}
              label="New Random Character"
              onClick={() => handleActionWithConfirm('random')}
            />

            {/* New From Template (collapsible) */}
            <button
              type="button"
              onClick={() => setTemplatesExpanded(!templatesExpanded)}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors text-left"
            >
              <span className="w-5 h-5 flex-shrink-0">
                <TemplateIcon />
              </span>
              <span className="text-sm font-medium flex-1">New From Template</span>
              <ChevronIcon
                className={clsx('w-4 h-4 transition-transform', templatesExpanded && 'rotate-180')}
              />
            </button>
            {templatesExpanded && (
              <div className="ml-8 space-y-1">
                <TemplateItem label="Aldric, Human Fighter" onClick={() => handleActionWithConfirm('template-fighter')} />
                <TemplateItem label="Elyndra, High Elf Wizard" onClick={() => handleActionWithConfirm('template-wizard')} />
                <TemplateItem label="Pippin, Halfling Rogue" onClick={() => handleActionWithConfirm('template-rogue')} />
                <TemplateItem label="Thorin, Dwarf Life Cleric" onClick={() => handleActionWithConfirm('template-cleric')} />
              </div>
            )}

            <MenuItem
              icon={<UploadIcon />}
              label="Import Character"
              onClick={() => handleActionWithConfirm('import')}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-border-default my-4" />

          {/* My Characters Section */}
          <div className="mb-4">
            <h3 className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
              My Characters
            </h3>
            <MenuItem
              icon={<LibraryIcon />}
              label="Character Library"
              to={`/${rulesetId}/library`}
              onClick={onClose}
            />
          </div>

          {/* Divider */}
          <div className="border-t border-border-default my-4" />

          {/* Reference Section */}
          <div className="mb-4">
            <h3 className="px-3 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
              Reference
            </h3>
            {/* Ignus lore pages first (only for Ignus ruleset) */}
            {rulesetId === 'ignus' && (
              <>
                <MenuItem icon={<FlameIcon />} label="Intro to Ignus" to="/ignus/intro" onClick={onClose} />
                <MenuItem icon={<MapIcon />} label="Geography" to="/ignus/geography" onClick={onClose} />
                <MenuItem icon={<FactionIcon />} label="Factions" to="/ignus/factions" onClick={onClose} />
                <MenuItem icon={<MagicIcon />} label="Magic & Titans" to="/ignus/magic" onClick={onClose} />
              </>
            )}
            {/* Core character creation references */}
            <MenuItem icon={<RaceIcon />} label={terminology.racePlural} to={`/${rulesetId}/races`} onClick={onClose} />
            <MenuItem icon={<ClassIcon />} label="Classes" to={`/${rulesetId}/classes`} onClick={onClose} />
            <MenuItem icon={<SpellBookIcon />} label="Spells" to={`/${rulesetId}/spells`} onClick={onClose} />
            <MenuItem icon={<SwordShieldIcon />} label="Weapons & Gear" to={`/${rulesetId}/weapons-gear`} onClick={onClose} />
            {/* Secondary references */}
            <MenuItem icon={<FeatIcon />} label="Feats" to={`/${rulesetId}/feats`} onClick={onClose} />
            <MenuItem icon={<ConditionIcon />} label="Conditions" to={`/${rulesetId}/conditions`} onClick={onClose} />
            <MenuItem icon={<FightingStyleIcon />} label="Fighting Styles" to={`/${rulesetId}/fighting-styles`} onClick={onClose} />
            <MenuItem icon={<BeastIcon />} label="Beasts" to={`/${rulesetId}/beasts`} onClick={onClose} />
          </div>

        </nav>
      </div>

      {/* Auto-Save Notification Dialog */}
      <AutoSaveNotificationDialog
        isOpen={showSaveNotification}
        onClose={() => {
          setShowSaveNotification(false);
          setPendingAction(null);
          setPendingRuleset(null);
        }}
        onContinue={() => {
          setShowSaveNotification(false);
          if (pendingAction) {
            executeAction(pendingAction);
          }
          setPendingAction(null);
          setPendingRuleset(null);
        }}
        characterName={savedCharacterName}
        rulesetId={rulesetId}
      />

      {/* Import Dialog */}
      <ImportCharacterDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={handleImport}
      />
    </>
  );
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  to?: string;
  onClick?: () => void;
}

function MenuItem({ icon, label, to, onClick }: MenuItemProps) {
  const className = clsx(
    'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg',
    'text-text-secondary hover:text-text-primary hover:bg-background-elevated',
    'transition-colors text-left'
  );

  if (to) {
    return (
      <Link to={to} className={className} onClick={onClick}>
        <span className="w-5 h-5 flex-shrink-0">{icon}</span>
        <span className="text-sm font-medium">{label}</span>
      </Link>
    );
  }

  return (
    <button type="button" className={className} onClick={onClick}>
      <span className="w-5 h-5 flex-shrink-0">{icon}</span>
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

function TemplateItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-background-elevated rounded-lg text-left transition-colors"
    >
      {label}
    </button>
  );
}

// Icons
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
      />
    </svg>
  );
}

function DiceIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  );
}

function TemplateIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function RaceIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function ClassIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  );
}

function SpellBookIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function SwordShieldIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

function SwitchIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
      />
    </svg>
  );
}

function ConditionIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function FeatIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}

function BeastIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );
}

function FightingStyleIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 21l3.75-3.75M3 21l6-6m-6 6v-4.5m0 4.5h4.5M21 3l-7.5 7.5m0 0l-3 3m3-3l-3-3m3 3l3 3"
      />
    </svg>
  );
}

function FlameIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
      />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
      />
    </svg>
  );
}

function FactionIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function MagicIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

function LibraryIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  );
}
